import React, { useMemo, useState } from 'react';
import { POLICIES, GROUPS } from '../data/mockData';
import { Ic, ICONS } from '../data/icons';
import { Card, CardHeader, Badge } from '../components/shared';

export default function Policies({ C }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [draft, setDraft] = useState({
    name: '',
    type: 'Güvenlik',
    platform: 'Tümü',
    targetGroupId: GROUPS[0]?.id ?? 1,
  });

  const filtered = useMemo(() => {
    return POLICIES.filter(p => {
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.type.toLowerCase().includes(q) ||
        p.platform.toLowerCase().includes(q);
      const matchesType = typeFilter === 'all' || p.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [search, typeFilter, statusFilter]);

  const handleDraftChange = (field, value) => {
    setDraft(d => ({ ...d, [field]: value }));
  };

  const typeOptions = ['Güvenlik', 'Güncelleme', 'Ağ'];
  const statusColor = status =>
    status === 'active'
      ? C.green
      : status === 'draft'
      ? C.amber
      : C.textSub;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Top row: filters + quick stats */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: '0 14px',
            height: 38,
            flex: '1 1 200px',
            maxWidth: 320,
          }}
        >
          <Ic d={ICONS.search} size={15} s={C.textFaint} />
          <input
            placeholder="Politika veya tür ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              background: 'none',
              border: 'none',
              outline: 'none',
              color: C.text,
              fontSize: 13,
              width: '100%',
              fontFamily: 'inherit',
            }}
          />
        </div>

        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          style={{
            height: 36,
            borderRadius: 9,
            border: `1px solid ${C.border}`,
            background: C.surface,
            color: C.text,
            fontSize: 12,
            padding: '0 10px',
            fontFamily: 'inherit',
          }}
        >
          <option value="all">Tüm Türler</option>
          {typeOptions.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            height: 36,
            borderRadius: 9,
            border: `1px solid ${C.border}`,
            background: C.surface,
            color: C.text,
            fontSize: 12,
            padding: '0 10px',
            fontFamily: 'inherit',
          }}
        >
          <option value="all">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="draft">Taslak</option>
        </select>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,2fr) minmax(0,1.3fr)',
          gap: 16,
        }}
      >
        {/* Policies table */}
        <Card C={C}>
          <CardHeader
            C={C}
            title="Politika Listesi"
            action={
              <span
                style={{
                  fontSize: 11,
                  color: C.accent,
                  cursor: 'default',
                  fontWeight: 600,
                }}
              >
                Toplam {POLICIES.length} politika
              </span>
            }
          />
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: 620,
              }}
            >
              <thead>
                <tr style={{ background: C.surface2 }}>
                  {[
                    'Politika',
                    'Tür',
                    'Platform',
                    'Hedef Gruplar',
                    'Durum',
                    'Son Güncelleme',
                  ].map(h => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '9px 16px',
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: C.textFaint,
                        letterSpacing: '0.7px',
                        textTransform: 'uppercase',
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const targetGroups = GROUPS.filter(g =>
                    p.targets.includes(g.id)
                  );
                  return (
                    <tr
                      key={p.id}
                      style={{
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e =>
                        (e.currentTarget.style.background = C.surface2)
                      }
                      onMouseLeave={e =>
                        (e.currentTarget.style.background = 'transparent')
                      }
                    >
                      <td
                        style={{
                          padding: '11px 16px',
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 8,
                              background: C.accentBg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Ic d={ICONS.shield} size={14} s={C.accent} />
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: C.text,
                              }}
                            >
                              {p.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '11px 16px',
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: C.textSub,
                          }}
                        >
                          {p.type}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '11px 16px',
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: C.textSub,
                          }}
                        >
                          {p.platform}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '11px 16px',
                          borderBottom: `1px solid ${C.border}`,
                          minWidth: 160,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 6,
                          }}
                        >
                          {targetGroups.map(g => (
                            <span
                              key={g.id}
                              style={{
                                fontSize: 11,
                                color: C.textSub,
                                padding: '3px 7px',
                                borderRadius: 999,
                                background: C.surface2,
                                border: `1px solid ${C.border}`,
                              }}
                            >
                              {g.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '11px 16px',
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        <Badge color={statusColor(p.status)}>
                          {p.status === 'active' ? 'Aktif' : 'Taslak'}
                        </Badge>
                      </td>
                      <td
                        style={{
                          padding: '11px 16px',
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            color: C.textFaint,
                          }}
                        >
                          {p.updatedAt}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: 40,
                        textAlign: 'center',
                        color: C.textFaint,
                        fontSize: 12,
                      }}
                    >
                      Kriterlere uyan politika bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* New policy form + groups overview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card C={C}>
            <CardHeader C={C} title="Yeni Politika Taslağı" />
            <div
              style={{
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: C.textFaint,
                    marginBottom: 4,
                  }}
                >
                  Politika Adı
                </div>
                <input
                  value={draft.name}
                  onChange={e => handleDraftChange('name', e.target.value)}
                  placeholder="Örn: Ekran kilidi zaman aşımı"
                  style={{
                    width: '100%',
                    borderRadius: 9,
                    border: `1px solid ${C.border}`,
                    background: C.surface2,
                    color: C.text,
                    fontSize: 12,
                    padding: '8px 10px',
                    fontFamily: 'inherit',
                    outline: 'none',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: '1 1 120px' }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.textFaint,
                      marginBottom: 4,
                    }}
                  >
                    Tür
                  </div>
                  <select
                    value={draft.type}
                    onChange={e =>
                      handleDraftChange('type', e.target.value)
                    }
                    style={{
                      width: '100%',
                      height: 34,
                      borderRadius: 9,
                      border: `1px solid ${C.border}`,
                      background: C.surface2,
                      color: C.text,
                      fontSize: 12,
                      padding: '0 9px',
                      fontFamily: 'inherit',
                    }}
                  >
                    {typeOptions.map(t => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ flex: '1 1 120px' }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.textFaint,
                      marginBottom: 4,
                    }}
                  >
                    Platform
                  </div>
                  <select
                    value={draft.platform}
                    onChange={e =>
                      handleDraftChange('platform', e.target.value)
                    }
                    style={{
                      width: '100%',
                      height: 34,
                      borderRadius: 9,
                      border: `1px solid ${C.border}`,
                      background: C.surface2,
                      color: C.text,
                      fontSize: 12,
                      padding: '0 9px',
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="Tümü">Tümü</option>
                    <option value="macOS">macOS</option>
                    <option value="Windows">Windows</option>
                    <option value="iOS/iPadOS">iOS/iPadOS</option>
                  </select>
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: C.textFaint,
                    marginBottom: 4,
                  }}
                >
                  Hedef Grup
                </div>
                <select
                  value={draft.targetGroupId}
                  onChange={e =>
                    handleDraftChange(
                      'targetGroupId',
                      Number(e.target.value)
                    )
                  }
                  style={{
                    width: '100%',
                    height: 34,
                    borderRadius: 9,
                    border: `1px solid ${C.border}`,
                    background: C.surface2,
                    color: C.text,
                    fontSize: 12,
                    padding: '0 9px',
                    fontFamily: 'inherit',
                  }}
                >
                  {GROUPS.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.name} · {g.devices} cihaz
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  fontSize: 11,
                  color: C.textFaint,
                  marginTop: 4,
                }}
              >
                Not: Bu form şu an taslak amaçlıdır; FAZ 6 ile gerçek API çağrılarına
                bağlanacaktır.
              </div>
            </div>
          </Card>

          <Card C={C}>
            <CardHeader C={C} title="Cihaz Grupları" />
            <div>
              {GROUPS.map(g => (
                <div
                  key={g.id}
                  style={{
                    padding: '10px 16px',
                    borderBottom: `1px solid ${C.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 999,
                        background: C.surface2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Ic
                        d={ICONS.users}
                        size={12}
                        s={C.textSub}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 12.5,
                          color: C.text,
                          fontWeight: 600,
                        }}
                      >
                        {g.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.textFaint,
                        }}
                      >
                        {g.devices} cihaz
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

